import { useTranslation } from 'react-i18next';
import { Loader2, Printer } from 'lucide-react';

import type { CertificateReceiptDialogProps } from './types';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';

import { useReceiptQuery } from '@/queries/certificate';

import { useSendReceiptToEmailMutation } from '@/mutations/certificate';

export const CertificateReceiptDialog = (
  props: CertificateReceiptDialogProps,
) => {
  const { isOpen, onClose, receipt, abilityToSendEmail = false } = props;
  const { t } = useTranslation();

  const { data: receiptHtml, isLoading: isReceiptLoading } = useReceiptQuery(
    receipt?.receipt_url || '',
  );

  const { mutate: sendReceiptToEmail, isPending: isSendingReceipt } =
    useSendReceiptToEmailMutation();

  const isLoading = isReceiptLoading || isSendingReceipt;

  const onReceiptSendToEmail = () => {
    if (!receipt?.send_receipt_url) {
      return;
    }
    sendReceiptToEmail(receipt.send_receipt_url);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] max-w-[calc(100%-2rem)] flex-col sm:max-w-lg"
        showCloseButton
      >
        <DialogTitle>{t('Receipt')}</DialogTitle>
        <DialogDescription>{t('Certificate receipt')}</DialogDescription>
        <div className="flex-1 overflow-auto">
          {receiptHtml && (
            <div
              className="receipt-wrapper"
              dangerouslySetInnerHTML={{ __html: receiptHtml }}
            />
          )}
          {isLoading && (
            <div className="hidden-on-print flex w-full items-center justify-center p-3">
              <Loader2 className="text-primary size-6 animate-spin" />
            </div>
          )}
        </div>

        <DialogFooter className="hidden-on-print">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {t('Close')}
          </Button>
          {abilityToSendEmail && receipt?.send_receipt_url && (
            <Button
              onClick={onReceiptSendToEmail}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {t("Send to client's email")}
            </Button>
          )}
          <Button
            onClick={() => window.print()}
            disabled={isLoading}
          >
            <Printer className="mr-2 size-4" />
            {t('Print')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export type {
  CertificateReceipt,
  CertificateReceiptDialogProps,
} from './types';
